import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSession,
  getSessionCalcData,
  getSessionSimple,
  getSessionSummary,
  getSessions,
  updateSession,
  deleteSession,
  getTyreReferenceSessions,
  getTyreReport,
  updateWeatherData,
  duplicateSession,
  setWheelAndReprocess,
  assignCrr,
  reorderConfigs,
  getMultiGateReferenceSessions,
  getMultiGate,
  getMultiGateProgress,
  setMultiGateGates,
  setMultiGateReference,
  syncMultiGate,
} from '../api/sessions';
import { Session } from '../types/Session';

export const useSessions = (queryString: string = '') =>
  useQuery({ queryKey: ['sessions', queryString], queryFn: () => getSessions(queryString) });

export const useSessionsWithConfigs = () =>
  useQuery({ queryKey: ['sessions', 'with_configs_min'], queryFn: () => getSessions('scope=with_configs_min') });

// Phase-1 summary payload for the SessionView config/run list (no heavy per-channel
// arrays). Distinct cache key so it never collides with the full ['session', id].
export const useSessionSummary = (sessionId: string) =>
  useQuery({
    queryKey: ['session-summary', sessionId],
    queryFn: (): Promise<Session> => getSessionSummary(sessionId),
    enabled: !!sessionId && sessionId !== 'no-session-selected',
  });

// Minimal payload for the SessionSimple page (freeride/discarded singletons).
export const useSessionSimple = (sessionId: string) =>
  useQuery({
    queryKey: ['session-simple', sessionId],
    queryFn: () => getSessionSimple(sessionId),
    enabled: !!sessionId && sessionId !== 'no-session-selected',
  });

// Full calc payload for plots. `includeRolling` opts into the tyre-plot-only delta
// variants and uses a distinct cache key so the base (deduped) variant is unaffected.
export const useSessionCalcData = (sessionId: string, opts?: { includeRolling?: boolean }) =>
  useQuery({
    queryKey: opts?.includeRolling ? ['session', sessionId, 'rolling'] : ['session', sessionId],
    queryFn: (): Promise<Session> => getSessionCalcData(sessionId, opts),
    enabled: !!sessionId && sessionId !== 'no-session-selected', // Only fetch if sessionId is valid
  });

export const useUpdateSession = (sessionId: string) =>
  useMutation({ mutationFn: (data: any) => updateSession(sessionId, data) });

export const useCreateSession = () => useMutation({ mutationFn: (data: any) => createSession(data) });

export const useSetSession = () => {
  const queryClient = useQueryClient();
  return (data: any) => {
    // Intentionally writes only the full ['session', id] cache (plot consumers).
    // Must not stomp the ['session-summary', id] cache that drives the list.
    queryClient.setQueryData(['session', data.id], data);
  };
};

export const useInvalidateSession = (sessionId: string) => {
  const queryClient = useQueryClient();
  return async () => {
    // Refresh both phases together: the full payload (base + the ['session', id,
    // 'rolling'] variant via prefix match) and the list summary.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['session', sessionId] }),
      queryClient.invalidateQueries({ queryKey: ['session-summary', sessionId] }),
      queryClient.invalidateQueries({ queryKey: ['session-simple', sessionId] }),
    ]);
  };
};

export const useDeleteSession = () => useMutation({ mutationFn: (sessionId: string) => deleteSession(sessionId) });

export const useTyreReferenceSessions = () =>
  useQuery({ queryKey: ['tyre-reference-sessions'], queryFn: getTyreReferenceSessions });

export const useSessionTyreReference = (sessionId: string) =>
  useQuery({
    queryKey: ['session-tyre-reference', sessionId],
    queryFn: () => getTyreReport(sessionId),
    enabled: !!sessionId,
  });

export const useUpdateWeatherData = (sessionId: string) =>
  useMutation({ mutationFn: () => updateWeatherData(sessionId) });

export const useDuplicateSession = () =>
  useMutation({
    mutationFn: (
      { sessionId, mode }:
      { sessionId: string; mode: 'with_runs' | 'without_runs' },
    ) => duplicateSession(sessionId, mode),
  });

export const useMultiGateReferenceSessions = () =>
  useQuery({ queryKey: ['sessions', 'multigate_reference_sessions'], queryFn: getMultiGateReferenceSessions });

export const useMultiGate = (sessionId: string) =>
  useQuery({
    queryKey: ['session-multigate', sessionId],
    queryFn: () => getMultiGate(sessionId),
    enabled: !!sessionId,
  });

export const useSetMultiGateGates = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, gatesIds }: { sessionId: string; gatesIds: string[] }) =>
      setMultiGateGates(sessionId, gatesIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session-multigate'] });
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
};

export const useSetMultiGateReference = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, gatesId }: { sessionId: string; gatesId: string }) =>
      setMultiGateReference(sessionId, gatesId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session-multigate'] });
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      queryClient.invalidateQueries({ queryKey: ['session'] });
    },
  });
};

export const useSyncMultiGate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, rebuild = false }: { sessionId: string; rebuild?: boolean }) =>
      syncMultiGate(sessionId, rebuild),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session-multigate'] });
      queryClient.invalidateQueries({ queryKey: ['session-multigate-progress'] });
    },
  });
};

/** Polls a group's sync progress: every 2 s while a sync is under way, every 15 s otherwise, so a sync
 * started elsewhere (autocal re-syncs a reference when it re-fits) still shows up. */
export const useMultiGateProgress = (sessionId: string) =>
  useQuery({
    queryKey: ['session-multigate-progress', sessionId],
    queryFn: () => getMultiGateProgress(sessionId),
    enabled: !!sessionId,
    refetchInterval: (query) => (query.state.data?.active ? 2000 : 15000),
  });

export const useSetWheelAndReprocess = (sessionId: string) =>
  useMutation({
    mutationFn: () => setWheelAndReprocess(sessionId),
  });

// Dynamic variant — session id is supplied per mutate call instead of at hook
// creation. Used by composite views that need to iterate across multiple
// sessions in a single user action (e.g. Tyre Reports Coordination).
export const useSetWheelAndReprocessDynamic = () =>
  useMutation({
    mutationFn: (sessionId: string) => setWheelAndReprocess(sessionId),
  });

export const useAssignCrr = (sessionId: string) =>
  useMutation({
    mutationFn: () => assignCrr(sessionId),
  });

export const useReorderConfigs = (sessionId: string) =>
  useMutation({
    mutationFn: (body: Record<string, any>) =>
      reorderConfigs(sessionId, body),
  });
